import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, AlertOctagon, Eye, XCircle, CheckCircle2, Zap, TrendingUp, FileText, ShieldCheck, ArrowRight, ChevronDown, ChevronRight, Info, Database, Shield, Activity } from 'lucide-react';
import { StatCard } from '../../../../shared/StatCard';
import { Badge } from '../../../../../components/ui/Badge';
import { Card } from '../../../../../components/ui/Card';
import { MODULE_CONFIG, PIPELINE_STAGES } from '../shared';
import type { GovernanceStats, ModuleHealth, FamilyHealth, FamilyTriageItem, UnifiedQuestionRow, SystemStats } from '../hooks/useGovernanceData';
import type { IssueDomain, ShadowImpactReport } from '../../../../../governance';
import { getGuardGovernanceIssues } from '../../../../../practice/logic/guardGovernanceBridge';
interface OverviewDashboardProps {
  rows: UnifiedQuestionRow[];
  stats: GovernanceStats;
  systemStats: SystemStats;
  moduleHealth: ModuleHealth[];
  familyHealthByModule: Record<string, FamilyHealth[]>;
  questionTypeDistribution: Map<string, number>;
  visualTypeDistribution: Map<string, number>;
  topBlockers: UnifiedQuestionRow[];
  familyTriage: FamilyTriageItem[];
  pipelineIssueCounts: Record<IssueDomain, number> | null;
  shadowImpactReport?: ShadowImpactReport | null;
  isRunning: boolean;
  onNavigateToQuestion: (filter?: string) => void;
}
export function OverviewDashboard({
  rows,
  stats,
  systemStats,
  moduleHealth,
  familyHealthByModule,
  questionTypeDistribution,
  visualTypeDistribution,
  topBlockers,
  familyTriage,
  pipelineIssueCounts,
  shadowImpactReport,
  isRunning,
  onNavigateToQuestion
}: OverviewDashboardProps) {
  const isHealthy = stats.fail === 0 && stats.review === 0;
  const [expandedModules, setExpandedModules] = useState<Set<string>>(new Set());
  const [expandedSeeds, setExpandedSeeds] = useState<Set<string>>(new Set());
  const [showOnlyModulesWithIssues, setShowOnlyModulesWithIssues] = useState(true);
  const [sortUrgency, setSortUrgency] = useState(true);
  const [isDiagnoseOpen, setIsDiagnoseOpen] = useState(true);
  const [isSysteemOpen, setIsSysteemOpen] = useState(false);
  // P-9: Guard → Governance feedback data
  const guardSummary = useMemo(() => getGuardGovernanceIssues(), []);
  const toggleModule = (code: string) => {
    setExpandedModules(prev => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);else next.add(code);
      return next;
    });
  };
  const toggleSeed = (familyId: string) => {
    setExpandedSeeds(prev => {
      const next = new Set(prev);
      if (next.has(familyId)) next.delete(familyId);else next.add(familyId);
      return next;
    });
  };
  const triageByModule = useMemo(() => {
    const map = new Map<string, FamilyTriageItem[]>();
    for (const f of familyTriage) {
      if (!map.has(f.moduleCode)) map.set(f.moduleCode, []);
      map.get(f.moduleCode)!.push(f);
    }
    return map;
  }, [familyTriage]);
  const moduleRows = useMemo(() => {
    let mods = moduleHealth.filter(m => {
      if (showOnlyModulesWithIssues) return triageByModule.has(m.code);
      return true;
    });
    if (sortUrgency) {
      mods.sort((a, b) => {
        const aUrgency = triageByModule.get(a.code)?.reduce((sum, f) => sum + f.urgencyScore, 0) || 0;
        const bUrgency = triageByModule.get(b.code)?.reduce((sum, f) => sum + f.urgencyScore, 0) || 0;
        return bUrgency - aUrgency;
      });
    } else {
      mods.sort((a, b) => a.code.localeCompare(b.code));
    }
    return mods;
  }, [moduleHealth, triageByModule, showOnlyModulesWithIssues, sortUrgency]);
  // Total findings across all pipeline stages
  const totalFindings = pipelineIssueCounts ? Object.values(pipelineIssueCounts).reduce((sum, c) => sum + c, 0) : 0;
  return <div className="relative max-w-7xl mx-auto pb-12" data-id="element-5361" data-mp="16ssz8kbw43vl">
      {/* Vertical Connector Line for Narrative Flow */}
      <div className="absolute left-4 top-8 bottom-8 w-px bg-gray-200 hidden sm:block" data-id="element-5362" data-mp="11uhqye18u1fh" />

      <div className="space-y-12" data-id="element-5363" data-mp="1lu9krnib4lpz">
        {/* ZONE A: Acties vandaag */}
        <section className="relative" data-id="element-5364" data-mp="1pvusyvjwcbtu">
          <header className="flex items-start gap-4 mb-6" data-id="element-5365" data-mp="1puthipyyxnbl">
            <div className="relative z-10 flex items-center justify-center w-8 h-8 rounded-full bg-orange-100 text-orange-600 text-sm font-bold border-4 border-white shrink-0 hidden sm:flex shadow-sm" data-id="element-5366" data-mp="1un33juwyq38l">
              1
            </div>
            <div className="pt-1" data-id="element-5367" data-mp="12xzwy8hlsgss">
              <h2 className="text-lg font-bold text-gray-900" data-id="element-5368" data-mp="16xs6anetmqz7">
                Acties Vandaag
              </h2>
              <p className="text-sm text-gray-500 mt-0.5" data-id="element-5369" data-mp="1bwbxp3c6ouyy">
                Wat vereist directe aandacht in de content pipeline?
              </p>
            </div>
          </header>

          <div className="sm:ml-12 space-y-6" data-id="element-5370" data-mp="2nc0j3q9shlxn">
            {/* Health Banner — question-level summary */}
            <div className={`flex items-center gap-4 px-5 py-4 rounded-2xl border shadow-sm transition-colors ${isHealthy ? 'bg-emerald-50/50 border-emerald-200' : stats.fail > 0 ? 'bg-red-50/50 border-red-200' : 'bg-amber-50/50 border-amber-200'}`} data-id="element-5371" data-mp="2sav0cns3z2fn">
              
              <div className={`p-2 rounded-full flex-shrink-0 ${isHealthy ? 'bg-emerald-100 text-emerald-600' : stats.fail > 0 ? 'bg-red-100 text-red-600' : 'bg-amber-100 text-amber-600'}`} data-id="element-5372" data-mp="bqroi1nwaze5">
                
                {isHealthy ? <ShieldCheck className="w-6 h-6" data-id="element-5373" data-mp="3u2o75md2w9i5" /> : stats.fail > 0 ? <XCircle className="w-6 h-6" data-id="element-5374" data-mp="3u3fte6kscuvr" /> : <AlertTriangle className="w-6 h-6" data-id="element-5375" data-mp="3u0bf2gdlwird" />}
              </div>
              <div className="flex-1 min-w-0" data-id="element-5376" data-mp="3p206sa2ymgb9">
                <span className={`text-base font-semibold ${isHealthy ? 'text-emerald-900' : stats.fail > 0 ? 'text-red-900' : 'text-amber-900'}`} data-id="element-5377" data-mp="3k48az4z72a1s">
                  
                  {isHealthy ? 'Alle vragen voldoen aan de kwaliteitseisen' : stats.fail > 0 ? `${stats.fail} ${stats.fail === 1 ? 'vraag' : 'vragen'} afgekeurd` : 'Aandachtspunten gevonden'}
                </span>
                {!isHealthy && <div className={`text-sm mt-0.5 ${stats.fail > 0 ? 'text-red-700/80' : 'text-amber-700/80'}`} data-id="element-5378" data-mp="37uioxtuoytrf">
                  
                    {[stats.review > 0 && `${stats.review} vereisen review`, totalFindings > 0 && `${totalFindings} bevindingen totaal`].filter(Boolean).join(' · ')}
                  </div>}
              </div>
            </div>

            <Card className="p-0 shadow-sm border-gray-200/60 overflow-hidden" data-id="element-5379" data-mp="32vfl2cqg6tl1">
              <div className="p-5 border-b border-gray-100 bg-white flex flex-col sm:flex-row sm:items-start justify-between gap-4" data-id="element-5380" data-mp="3qm1qdw92hzrc">
                <div data-id="element-5381" data-mp="3vhh3un35xtn8">
                  <h3 className="text-sm font-semibold text-gray-900" data-id="element-5382" data-mp="3hkc04tqx96v5">
                    Werklijst — Module → Seed → Vraag
                  </h3>
                  <p className="text-[11px] text-gray-500 mt-1 max-w-2xl" data-id="element-5383" data-mp="3lk2m8k5i6ytc">
                    Open een module om de probleem-seeds te zien. Open een seed
                    om de afgekeurde vragen te zien. Klik op een vraag voor
                    details.
                  </p>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0" data-id="element-5384" data-mp="22lei7ts9gkcc">
                  <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer hover:text-gray-900" data-id="element-5385" data-mp="1xmuupoy0r0hv">
                    <input type="checkbox" checked={showOnlyModulesWithIssues} onChange={e => setShowOnlyModulesWithIssues(e.target.checked)} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-id="element-5386" data-mp="2ckn9ufznlmvj" />
                    
                    Alleen issues
                  </label>
                  <select value={sortUrgency ? 'urgency' : 'alpha'} onChange={e => setSortUrgency(e.target.value === 'urgency')} className="text-xs border-gray-200 rounded-md text-gray-600 py-1 pl-2 pr-6 focus:ring-blue-500 focus:border-blue-500" data-id="element-5387" data-mp="1eypyr3nlsjtm">
                    
                    <option value="urgency" data-id="element-5388" data-mp="1156yld6sdn4c">Sorteer: Urgentie</option>
                    <option value="alpha" data-id="element-5389" data-mp="w6x311h5gki7">Sorteer: A-Z</option>
                  </select>
                </div>
              </div>

              <div className="divide-y divide-gray-100" data-id="element-5390" data-mp="aodr13y5l8u9">
                {moduleRows.length === 0 ? <div className="p-8 text-center text-gray-500 text-sm" data-id="element-5391" data-mp="fmfbntb89g7u">
                    Geen modules gevonden met de huidige filters.
                  </div> : moduleRows.map(mod => {
                const isExpanded = expandedModules.has(mod.code);
                const modTriage = triageByModule.get(mod.code) || [];
                const config = MODULE_CONFIG[mod.code];
                const bgClass = config?.bgClass ?? 'bg-gray-400';
                const criticalCount = modTriage.reduce((sum, f) => sum + f.criticalCount, 0);
                const blockerCount = modTriage.reduce((sum, f) => sum + f.blockerCount, 0);
                const reviewCount = modTriage.reduce((sum, f) => sum + f.reviewCount, 0);
                const worstPct = modTriage.length > 0 ? Math.min(...modTriage.map(f => f.avgScore)) : 100;
                return <div key={mod.code} className="bg-white" data-id="element-5392" data-mp="1hcsrv4p2hmnx">
                        <div className="flex items-center justify-between p-3 hover:bg-gray-50 cursor-pointer transition-colors" onClick={() => toggleModule(mod.code)} data-id="element-5393" data-mp="31ul2p2aml81c">
                        
                          <div className="flex items-center gap-3" data-id="element-5394" data-mp="2st3o5ocv9eer">
                            <div className={`w-2.5 h-2.5 rounded-full ${bgClass}`} data-id="element-5395" data-mp="2svhl62r3x0oy" />
                          
                            <Badge variant="neutral" className="font-mono font-bold" data-id="element-5396" data-mp="3kkjmgfnscpkt">
                            
                              {mod.code}
                            </Badge>
                            <span className="text-sm text-gray-600 hidden sm:inline" data-id="element-5397" data-mp="3glbvack1kb5i">
                              {mod.name}
                            </span>
                          </div>
                          <div className="flex items-center gap-4" data-id="element-5398" data-mp="36r22itr6tau9">
                            <div className="hidden md:flex items-center gap-1.5" data-id="element-5399" data-mp="83xv534xp15d">
                              {criticalCount > 0 && <span className="text-[10px] font-semibold text-red-700 bg-red-50 border border-red-100 px-1.5 py-0.5 rounded" data-id="element-5400" data-mp="3av0erof7lnl">
                                  {criticalCount} critical
                                </span>}
                              {blockerCount > 0 && <span className="text-[10px] font-semibold text-orange-700 bg-orange-50 border border-orange-100 px-1.5 py-0.5 rounded" data-id="element-5401" data-mp="20pr3u15e6yhx">
                                  {blockerCount} blocker
                                </span>}
                              {reviewCount > 0 && <span className="text-[10px] font-semibold text-yellow-700 bg-yellow-50 border border-yellow-100 px-1.5 py-0.5 rounded" data-id="element-5402" data-mp="2al2jqmsjebr9">
                                  {reviewCount} review
                                </span>}
                            </div>
                            <div className="text-right text-xs text-gray-500 w-32" data-id="element-5403" data-mp="2fex8w5qwvixq">
                              {modTriage.length > 0 ? <>
                                  <span className="font-medium text-gray-900" data-id="element-5404" data-mp="2ofbxbdz69e5g">
                                    {modTriage.length}
                                  </span>{' '}
                                  van{' '}
                                  {familyHealthByModule[mod.code]?.length || 0}{' '}
                                  seeds
                                  <div className="text-[10px] text-red-500" data-id="element-5405" data-mp="2yznqap6urpe0">
                                    worst: {worstPct}%
                                  </div>
                                </> : <span className="text-emerald-600 font-medium" data-id="element-5406" data-mp="2p92s32v3cmvm">
                                  Alle{' '}
                                  {familyHealthByModule[mod.code]?.length || 0}{' '}
                                  seeds OK
                                </span>}
                            </div>
                            <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} data-id="element-5407" data-mp="2kbasrns49yhe" />
                          
                          </div>
                        </div>

                        <AnimatePresence data-id="element-5408" data-mp="2bc5t2e4tjz3n">
                          {isExpanded && modTriage.length > 0 && <motion.div initial={{
                      height: 0,
                      opacity: 0
                    }} animate={{
                      height: 'auto',
                      opacity: 1
                    }} exit={{
                      height: 0,
                      opacity: 0
                    }} transition={{
                      duration: 0.2
                    }} className="overflow-hidden bg-gray-50/30 border-t border-gray-100" data-id="element-5409" data-mp="gvfmotnf3jxq">
                          
                              <div className="divide-y divide-gray-100/50" data-id="element-5410" data-mp="us2szn19fmgb">
                                {modTriage.map((fam, idx) => {
                          const isSeedExpanded = expandedSeeds.has(fam.familyId);
                          return <div key={fam.familyId} data-id="element-5411" data-mp="3bi9np6hyv178" data-mpi={idx}>
                                      <div className="flex items-center justify-between p-2.5 pl-4 sm:pl-12 hover:bg-orange-50/40 transition-colors group relative" data-id="element-5412" data-mp="3bjui8w4a6k53">
                                        <div className="hidden sm:block absolute left-6 top-0 bottom-0 w-px bg-gray-200" data-id="element-5413" data-mp="1m2gqj93lk5qo" />
                                        <div className="hidden sm:block absolute left-6 top-1/2 w-4 h-px bg-gray-200" data-id="element-5414" data-mp="1r10ipr8sadrh" />

                                        <div className="flex items-center gap-3 min-w-0" data-id="element-5415" data-mp="1v098kl0pdrhr">
                                          <div className="flex-shrink-0 w-5 h-5 rounded-full bg-white border border-gray-200 text-[9px] font-bold text-gray-500 flex items-center justify-center shadow-sm" data-id="element-5416" data-mp="1zuwz0pf9ovyz">
                                            {idx + 1}
                                          </div>
                                          <div className="min-w-0" data-id="element-5417" data-mp="2eo8khz7074or">
                                            <div className="flex items-center gap-2" data-id="element-5418" data-mp="2ir4w35y4m0j7">
                                              <span className="font-mono text-xs font-bold text-gray-900" data-id="element-5419" data-mp="kd3i0y7nkyb0">
                                                {fam.familyId}
                                              </span>
                                              <div className="hidden lg:flex items-center gap-1" data-id="element-5420" data-mp="3p71pvf6dpud2">
                                                {fam.topIssueCodes.slice(0, 2).map(code => <span key={code} className="text-[9px] font-mono text-gray-500 bg-white border border-gray-200 px-1 rounded truncate max-w-[120px]" title={code} data-id="element-5421" data-mp="31eikcgzfojn">
                                              
                                                      {code}
                                                    </span>)}
                                              </div>
                                            </div>
                                            <div className="text-[10px] text-gray-500 mt-0.5" data-id="element-5422" data-mp="3g9hps9k96441">
                                              {fam.total} vragen ·{' '}
                                              <span className="text-emerald-600" data-id="element-5423" data-mp="2nqu8z6cahb8r">
                                                {fam.pass} pass
                                              </span>
                                              {fam.fail > 0 && <span className="text-red-600" data-id="element-5424" data-mp="y973qv7qyi5j">
                                                  {' '}
                                                  · {fam.fail} fail
                                                </span>}
                                              {fam.review > 0 && <span className="text-amber-600" data-id="element-5425" data-mp="tcp0y0vz8tvu">
                                                  {' '}
                                                  · {fam.review} review
                                                </span>}
                                            </div>
                                          </div>
                                        </div>

                                        <div className="flex items-center gap-4 flex-shrink-0" data-id="element-5426" data-mp="1cybks92t2gc2">
                                          <div className="hidden md:flex items-center gap-1" data-id="element-5427" data-mp="3spr5metit7kq">
                                            {fam.criticalCount > 0 && <span className="text-[10px] font-semibold text-red-700 bg-red-50 border border-red-100 px-1.5 py-0.5 rounded" title="Kritiek" data-id="element-5428" data-mp="1lsnay23el7i">
                                          
                                                <AlertOctagon className="w-3 h-3 inline mr-0.5" data-id="element-5429" data-mp="1ycn6b76r50md" />
                                                {fam.criticalCount}
                                              </span>}
                                            {fam.blockerCount > 0 && <span className="text-[10px] font-semibold text-orange-700 bg-orange-50 border border-orange-100 px-1.5 py-0.5 rounded" title="Blocker" data-id="element-5430" data-mp="1kjfg9jybkd6s">
                                          
                                                <XCircle className="w-3 h-3 inline mr-0.5" data-id="element-5431" data-mp="1alhtz200sqgt" />
                                                {fam.blockerCount}
                                              </span>}
                                            {fam.reviewCount > 0 && <span className="text-[10px] font-semibold text-yellow-700 bg-yellow-50 border border-yellow-100 px-1.5 py-0.5 rounded" title="Review" data-id="element-5432" data-mp="34wo6fpszxiku">
                                          
                                                <Eye className="w-3 h-3 inline mr-0.5" data-id="element-5433" data-mp="ro7jn3yiw3ek" />
                                                {fam.reviewCount}
                                              </span>}
                                          </div>
                                          <div className="w-10 text-right hidden sm:block" data-id="element-5434" data-mp="10g9gks9dv1k3">
                                            <span className={`text-xs font-bold ${fam.avgScore >= 80 ? 'text-emerald-600' : fam.avgScore >= 60 ? 'text-amber-600' : 'text-red-600'}`} data-id="element-5435" data-mp="3smpzayzqdek">
                                          
                                              {fam.avgScore}%
                                            </span>
                                          </div>
                                          <div className="flex items-center gap-2" data-id="element-5436" data-mp="1o5u5rsw4qv1t">
                                            <button onClick={() => onNavigateToQuestion(fam.familyId)} className="text-[10px] font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 px-2 py-1 rounded transition-colors hidden sm:block" data-id="element-5437" data-mp="1t4ox5j85xrxj">
                                          
                                              Open in Vragen →
                                            </button>
                                            <button onClick={() => toggleSeed(fam.familyId)} className="p-1 rounded hover:bg-gray-200 transition-colors" data-id="element-5438" data-mp="18uhyzc2mv2ne">
                                          
                                              <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${isSeedExpanded ? 'rotate-180' : ''}`} data-id="element-5439" data-mp="1ilcmejrm88af" />
                                          
                                            </button>
                                          </div>
                                        </div>
                                      </div>

                                      <AnimatePresence data-id="element-5440" data-mp="3ualthb4jm0sg">
                                        {isSeedExpanded && <motion.div initial={{
                                height: 0,
                                opacity: 0
                              }} animate={{
                                height: 'auto',
                                opacity: 1
                              }} exit={{
                                height: 0,
                                opacity: 0
                              }} transition={{
                                duration: 0.2
                              }} className="overflow-hidden bg-white border-y border-gray-100/50" data-id="element-5441" data-mp="3k65gqqmd0zsv">
                                      
                                            <div className="py-2 space-y-1 relative" data-id="element-5442" data-mp="2rijbzf1ebp4f">
                                              <div className="hidden sm:block absolute left-6 top-0 bottom-0 w-px bg-gray-200" data-id="element-5443" data-mp="2wft2yz4fo8ud" />

                                              {(() => {
                                    const qs = rows.filter(r => r.familyId === fam.familyId && (r.verdict !== 'PASS' || r.blockerCount > 0 || r.warningCount > 0));
                                    const displayQs = qs.slice(0, 5);
                                    return <>
                                                    {displayQs.map(q => {
                                        const firstBlocker = q.pipelineIssues.find(i => i.kind === 'blocker' || i.kind === 'critical') || q.pipelineIssues.find(i => i.kind === 'coach');
                                        return <div key={q.questionId} onClick={() => onNavigateToQuestion(q.questionId)} className="ml-4 sm:ml-20 mr-4 p-2 rounded-lg border border-gray-100 hover:border-red-200 hover:bg-red-50/40 cursor-pointer transition-colors flex items-center gap-3 group" data-id="element-5444" data-mp="2h0exkibp4q6r">
                                                    
                                                          <div className="w-1.5 h-1.5 rounded-full bg-red-400 flex-shrink-0" data-id="element-5445" data-mp="wjioe797ags8" />
                                                          <div className="min-w-0 flex-1" data-id="element-5446" data-mp="10mf2bknspopu">
                                                            <div className="flex items-center gap-2 mb-0.5" data-id="element-5447" data-mp="t1359e1m08z8">
                                                              <span className="font-mono text-[11px] font-bold text-gray-900 group-hover:text-blue-600 transition-colors" data-id="element-5448" data-mp="o28bxi60n20v">
                                                                {q.questionId}
                                                              </span>
                                                              <Badge variant={q.verdict === 'FAIL' ? 'danger' : 'warning'} size="sm" data-id="element-5449" data-mp="abwgpae4wn5i">
                                                          
                                                                {q.verdict}
                                                              </Badge>
                                                              <span className="text-[9px] text-gray-500 font-medium hidden sm:inline" data-id="element-5450" data-mp="3nq9mnzm9psjo">
                                                                {q.blockerCount}{' '}
                                                                blocker
                                                                {q.blockerCount !== 1 ? 's' : ''}
                                                                {q.warningCount > 0 && ` · ${q.warningCount} waarschuwing${q.warningCount !== 1 ? 'en' : ''}`}
                                                              </span>
                                                            </div>
                                                            {firstBlocker && <p className="text-[11px] text-gray-600 truncate" data-id="element-5451" data-mp="3ti1p6dazseyi">
                                                                <span className="font-mono font-semibold text-[9px] bg-gray-100 px-1 py-0.5 rounded text-gray-500 mr-1.5" data-id="element-5452" data-mp="2bqqgntgfqac">
                                                                  {firstBlocker.code}
                                                                </span>
                                                                {firstBlocker.message}
                                                              </p>}
                                                          </div>
                                                          <ArrowRight className="w-3 h-3 text-gray-300 group-hover:text-red-500 group-hover:translate-x-0.5 transition-all flex-shrink-0" data-id="element-5453" data-mp="2y9zmkvd90mij" />
                                                        </div>;
                                      })}
                                                    {qs.length > 5 && <div className="ml-4 sm:ml-20 mr-4 pt-1" data-id="element-5454" data-mp="2kfq7fzqytc28">
                                                        <button onClick={() => onNavigateToQuestion(fam.familyId)} className="text-[10px] font-medium text-gray-500 hover:text-blue-600 flex items-center gap-1" data-id="element-5455" data-mp="2fkt2p50pxov5">
                                                  
                                                          + {qs.length - 5} meer
                                                          vragen — open seed in
                                                          Vragen tab{' '}
                                                          <ArrowRight className="w-3 h-3" data-id="element-5456" data-mp="bun2ala6z98q" />
                                                        </button>
                                                      </div>}
                                                  </>;
                                  })()}
                                            </div>
                                          </motion.div>}
                                      </AnimatePresence>
                                    </div>;
                        })}
                              </div>
                            </motion.div>}
                        </AnimatePresence>
                      </div>;
              })}
              </div>
            </Card>
          </div>
        </section>

        {/* ZONE B: Diagnose */}
        <section className="relative" data-id="element-5457" data-mp="36afefap1uv5k">
          <header className="flex items-start gap-4 mb-6" data-id="element-5458" data-mp="3abhflx2j1ha6">
            <div className="relative z-10 flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-blue-600 text-sm font-bold border-4 border-white shrink-0 hidden sm:flex shadow-sm" data-id="element-5459" data-mp="3aazeon4x6evx">
              2
            </div>
            <div className="pt-1 w-full" data-id="element-5460" data-mp="2mqggx4tcexzq">
              <button onClick={() => setIsDiagnoseOpen(!isDiagnoseOpen)} className="flex items-center justify-between w-full text-left group" data-id="element-5461" data-mp="30oy2owzou0f0">
                
                <div data-id="element-5462" data-mp="3am2qt2d3g5yg">
                  <h2 className="text-lg font-bold text-gray-900 group-hover:text-blue-600 transition-colors" data-id="element-5463" data-mp="3aljcdvmnjzp6">
                    Diagnose
                  </h2>
                  <p className="text-sm text-gray-500 mt-0.5" data-id="element-5464" data-mp="35l5aywfk3o04">
                    Waar in de pipeline ontstaan de bevindingen?
                  </p>
                </div>
                <div className="p-2 rounded-full hover:bg-gray-100 transition-colors" data-id="element-5465" data-mp="3fdb1e1q5txd7">
                  {isDiagnoseOpen ? <ChevronDown className="w-5 h-5 text-gray-400" data-id="element-5466" data-mp="3tctylzhfnoaq" /> : <ChevronRight className="w-5 h-5 text-gray-400" data-id="element-5467" data-mp="3odzfsxjgtzqm" />}
                </div>
              </button>
            </div>
          </header>

          <AnimatePresence initial={false} data-id="element-5468" data-mp="2m1w8ieklz97e">
            {isDiagnoseOpen && <motion.div initial={{
            opacity: 0,
            height: 0
          }} animate={{
            opacity: 1,
            height: 'auto'
          }} exit={{
            opacity: 0,
            height: 0
          }} transition={{
            duration: 0.2
          }} className="sm:ml-12 overflow-hidden" data-id="element-5469" data-mp="2q0v6ff7xa5pc">
              
                <div className="pb-2" data-id="element-5470" data-mp="34snbmye063dc">
                  <div className="space-y-6" data-id="element-5471" data-mp="39p4b2oqnk45q">
                    {/* Pipeline Stages */}
                    <Card className="p-5 shadow-sm border-gray-200/60" data-id="element-5472" data-mp="3drh7gmjieppu">
                      <div className="mb-5" data-id="element-5473" data-mp="3dpehg2icwwl4">
                        <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2" data-id="element-5474" data-mp="3iqk8zx0a6dt2">
                          <Activity className="w-4 h-4 text-blue-500" data-id="element-5475" data-mp="1je9ivcm4nacn" />{' '}
                          Pipeline Fasen
                        </h3>
                        <p className="text-[11px] text-gray-500 mt-1" data-id="element-5476" data-mp="1oedrkggsn0sb">
                          Aantal individuele bevindingen per controle-fase.
                        </p>
                      </div>
                      <div className="space-y-2.5" data-id="element-5477" data-mp="1c08lvaw0pskc">
                        {PIPELINE_STAGES.map(stage => {
                      const count = pipelineIssueCounts?.[stage.domain] ?? 0;
                      const status = !pipelineIssueCounts ? 'pending' : count === 0 ? 'ok' : count <= 5 ? 'warning' : 'error';
                      const statusConfig = {
                        ok: {
                          dot: 'bg-emerald-500',
                          border: 'border-emerald-100',
                          bg: 'bg-emerald-50/30'
                        },
                        warning: {
                          dot: 'bg-amber-500',
                          border: 'border-amber-100',
                          bg: 'bg-amber-50/30'
                        },
                        error: {
                          dot: 'bg-red-500',
                          border: 'border-red-100',
                          bg: 'bg-red-50/30'
                        },
                        pending: {
                          dot: 'bg-gray-300',
                          border: 'border-gray-100',
                          bg: 'bg-gray-50/50'
                        }
                      }[status];
                      return <div key={stage.name} className={`flex items-center justify-between p-3 rounded-xl border transition-colors ${statusConfig.bg} ${statusConfig.border}`} data-id="element-5478" data-mp="2t5p0mwq86k6b">
                            
                              <div className="flex items-center gap-3" data-id="element-5479" data-mp="2jgfbnpqohhmf">
                                <div className={`w-2 h-2 rounded-full shadow-sm ${statusConfig.dot}`} data-id="element-5480" data-mp="2oeqjxw1brk2j" />
                              
                                <span className="text-sm font-medium text-gray-700" data-id="element-5481" data-mp="3c39tsg00w4wq">
                                  {stage.label}
                                </span>
                              </div>
                              {status !== 'pending' && <Badge variant={status === 'ok' ? 'success' : status === 'warning' ? 'warning' : 'danger'} size="sm" data-id="element-5482" data-mp="3q80egipk66dt">
                              
                                  {count === 0 ? '0 bevindingen' : `${count} bevindingen`}
                                </Badge>}
                            </div>;
                    })}
                      </div>
                    </Card>

                    {/* P-9: Guard Runtime Rejections */}
                    <Card className="p-5 shadow-sm border-gray-200/60" data-id="element-5483" data-mp="33a5jlyz04hec">
                      <h3 className="text-sm font-semibold text-gray-900 mb-1 flex items-center gap-2" data-id="element-5484" data-mp="3h953ktbkk846">
                        <Shield className="w-4 h-4 text-orange-500" data-id="element-5485" data-mp="3cabq30wa8h40" /> Runtime
                        Guard
                      </h3>
                      <p className="text-[11px] text-gray-500 mb-5" data-id="element-5486" data-mp="whmkt8rn0g76">
                        Vragen afgewezen door de pedagogische guard tijdens live
                        oefensessies.
                      </p>

                      {guardSummary.totalRejections === 0 ? <div className="bg-emerald-50/50 rounded-xl p-5 text-center border border-emerald-100" data-id="element-5487" data-mp="2blexnqogo4he">
                          <CheckCircle2 className="w-6 h-6 text-emerald-400 mx-auto mb-2" data-id="element-5488" data-mp="27jjzgv1tu77u" />
                          <p className="text-xs font-medium text-emerald-800" data-id="element-5489" data-mp="22l08gg8dm58m">
                            Geen runtime afwijzingen
                          </p>
                        </div> : <div className="space-y-4" data-id="element-5490" data-mp="2zd3tvi4eld3m">
                          <div className="grid grid-cols-2 gap-3" data-id="element-5491" data-mp="2v95vj9mwk6bm">
                            <div className="bg-red-50/50 rounded-xl p-3 border border-red-100 text-center" data-id="element-5492" data-mp="2qipfrivm08m3">
                              <p className="text-xl font-bold text-red-700" data-id="element-5493" data-mp="2ljumto3xh3o9">
                                {guardSummary.totalAffectedQuestions}
                              </p>
                              <p className="text-[10px] font-medium text-red-600/80 uppercase tracking-wider mt-0.5" data-id="element-5494" data-mp="3j0tgzc1eap6i">
                                Vragen
                              </p>
                            </div>
                            <div className="bg-orange-50/50 rounded-xl p-3 border border-orange-100 text-center" data-id="element-5495" data-mp="3qhznvlrkb29y">
                              <p className="text-xl font-bold text-orange-700" data-id="element-5496" data-mp="3vhcjow0evjoo">
                                {guardSummary.totalRejections}
                              </p>
                              <p className="text-[10px] font-medium text-orange-600/80 uppercase tracking-wider mt-0.5" data-id="element-5497" data-mp="8d64vdjisd9y">
                                Afwijzingen
                              </p>
                            </div>
                          </div>

                          {guardSummary.reasonBreakdown.length > 0 && <div className="space-y-2" data-id="element-5498" data-mp="n2ao8y21wnjn">
                              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider" data-id="element-5499" data-mp="n3vjyr03qcj3">
                                Top redenen
                              </p>
                              {guardSummary.reasonBreakdown.slice(0, 3).map(r => <div key={r.reason} className="flex items-start gap-2 text-[11px]" data-id="element-5500" data-mp="2s5c7uparekth">
                          
                                    <span className="font-mono text-red-600 font-semibold flex-shrink-0 bg-red-50 px-1 rounded" data-id="element-5501" data-mp="2o5bkug9lh1at">
                                      {r.count}×
                                    </span>
                                    <span className="text-gray-600 leading-tight pt-0.5" data-id="element-5502" data-mp="2x3ds3djx8q6j">
                                      {r.description}
                                    </span>
                                  </div>)}
                            </div>}

                          {guardSummary.issues.length > 0 && <div className="space-y-2" data-id="element-5503" data-mp="3ktrcchw9lkqk">
                              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider" data-id="element-5504" data-mp="3fuoi7shtoyxd">
                                Meest afgewezen
                              </p>
                              {guardSummary.issues.slice(0, 3).map(issue => <div key={issue.questionId} className="flex items-center justify-between text-[10px] bg-gray-50 px-2 py-1.5 rounded-lg border border-gray-100" data-id="element-5505" data-mp="1ligogih8mo36">
                          
                                  <span className="font-mono font-medium text-gray-700 truncate mr-2" title={issue.questionId} data-id="element-5506" data-mp="5xpo833dxyzr">
                            
                                    {issue.questionId}
                                  </span>
                                  <div className="flex items-center gap-1.5 flex-shrink-0" data-id="element-5507" data-mp="3eeaqz25fbvuf">
                                    {issue.fatalCount > 0 && <span className="text-red-600 font-bold bg-red-100/50 px-1.5 rounded" data-id="element-5508" data-mp="3jddsicm73jxn">
                                        {issue.fatalCount}F
                                      </span>}
                                    {issue.softCount > 0 && <span className="text-amber-600 font-bold bg-amber-100/50 px-1.5 rounded" data-id="element-5509" data-mp="39f7sk0jy8nvf">
                                        {issue.softCount}S
                                      </span>}
                                  </div>
                                </div>)}
                            </div>}

                          {guardSummary.dataRange && <p className="text-[10px] text-gray-400 pt-2 border-t border-gray-100 text-center" data-id="element-5510" data-mp="dxaovss9hxts">
                              Data:{' '}
                              {new Date(guardSummary.dataRange.from).toLocaleDateString('nl-NL')}{' '}
                              —{' '}
                              {new Date(guardSummary.dataRange.to).toLocaleDateString('nl-NL')}
                            </p>}
                        </div>}
                    </Card>
                  </div>
                </div>
              </motion.div>}
          </AnimatePresence>
        </section>

        {/* ZONE C: Systeem-overzicht */}
        <section className="relative" data-id="element-5511" data-mp="3hfvrij8h9rfe">
          <header className="flex items-start gap-4 mb-6" data-id="element-5512" data-mp="3cfgddpgdgfwd">
            <div className="relative z-10 flex items-center justify-center w-8 h-8 rounded-full bg-slate-100 text-slate-600 text-sm font-bold border-4 border-white shrink-0 hidden sm:flex shadow-sm" data-id="element-5513" data-mp="3cey7s27tkpbs">
              3
            </div>
            <div className="pt-1 w-full" data-id="element-5514" data-mp="1ny6eribiraoz">
              <button onClick={() => setIsSysteemOpen(!isSysteemOpen)} className="flex items-center justify-between w-full text-left group" data-id="element-5515" data-mp="1rwdy8kxdaq3v">
                
                <div data-id="element-5516" data-mp="21vlat5wa2gpx">
                  <h2 className="text-lg font-bold text-gray-900 group-hover:text-slate-600 transition-colors" data-id="element-5517" data-mp="21w4scli0n33f">
                    Systeem-overzicht
                  </h2>
                  <p className="text-sm text-gray-500 mt-0.5" data-id="element-5518" data-mp="14606kvip2t9t">
                    Hoe staat het systeem er in zijn totaliteit voor?
                  </p>
                </div>
                <div className="p-2 rounded-full hover:bg-gray-100 transition-colors" data-id="element-5519" data-mp="1e4pogoalcdq2">
                  {isSysteemOpen ? <ChevronDown className="w-5 h-5 text-gray-400" data-id="element-5520" data-mp="1iytv16fy2jqm" /> : <ChevronRight className="w-5 h-5 text-gray-400" data-id="element-5521" data-mp="li4x3nu481mi" />}
                </div>
              </button>
            </div>
          </header>

          <AnimatePresence initial={false} data-id="element-5522" data-mp="qed76mtgh4mt">
            {isSysteemOpen && <motion.div initial={{
            opacity: 0,
            height: 0
          }} animate={{
            opacity: 1,
            height: 'auto'
          }} exit={{
            opacity: 0,
            height: 0
          }} transition={{
            duration: 0.2
          }} className="sm:ml-12 overflow-hidden" data-id="element-5523" data-mp="iep1yst9xjdx">
              
                <div className="space-y-6 pb-2" data-id="element-5524" data-mp="n8t8jaymnpeh">
                  {/* Stats — question-level counts */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-5525" data-mp="3vo3fawdgpisl">
                    <StatCard label="Totaal Vragen" value={stats.total.toLocaleString('nl-NL')} icon={<FileText className="w-5 h-5" data-id="element-5527" data-mp="9gdbqrwbknm0" />} color="blue" subtitle="Unieke vragen in het systeem" data-id="element-5526" data-mp="3rn1gggngj8r5" />
                  
                    <StatCard label="Geslaagd (PASS)" value={stats.pass.toLocaleString('nl-NL')} icon={<CheckCircle2 className="w-5 h-5" data-id="element-5529" data-mp="1kwbpva6ctzu3" />} color="emerald" subtitle={`${Math.round(stats.pass / Math.max(stats.total, 1) * 100)}% van alle vragen`} data-id="element-5528" data-mp="1ku8qw0n84wd8" />
                  
                    <StatCard label="Afgekeurd (FAIL)" value={stats.fail.toLocaleString('nl-NL')} icon={<XCircle className="w-5 h-5" data-id="element-5531" data-mp="32sn9aygr4ltx" />} color={stats.fail === 0 ? 'emerald' : 'red'} subtitle={`${stats.blockerCount} blocker-bevindingen`} data-id="element-5530" data-mp="2xr7ptau82ndn" />
                  
                    <StatCard label="Review Nodig" value={stats.review.toLocaleString('nl-NL')} icon={<AlertTriangle className="w-5 h-5" data-id="element-5533" data-mp="3hh06tzvhmcqj" />} color={stats.review === 0 ? 'emerald' : 'amber'} subtitle={`${stats.warningCount} verbeterpunten`} data-id="element-5532" data-mp="2tqnt28dk9ls1" />
                  
                  </div>

                  {/* 5-level severity tiles */}
                  <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5" data-id="element-5534" data-mp="37qfayk777mbb">
                    <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-4" data-id="element-5535" data-mp="3coqigoa0tkqd">
                      Bevindingen per ernst
                    </h3>
                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-id="element-5536" data-mp="91ged32n4ycj">
                      {[{
                    key: 'critical' as const,
                    label: 'Kritiek',
                    Icon: AlertOctagon,
                    bg: 'bg-red-50/50',
                    border: 'border-red-100',
                    text: 'text-red-700',
                    iconClass: 'text-red-600',
                    hint: 'Blocker · ernst ≥90'
                  }, {
                    key: 'blocker' as const,
                    label: 'Blocker',
                    Icon: XCircle,
                    bg: 'bg-orange-50/50',
                    border: 'border-orange-100',
                    text: 'text-orange-700',
                    iconClass: 'text-orange-600',
                    hint: 'Blocker · ernst 70-89'
                  }, {
                    key: 'review' as const,
                    label: 'Review',
                    Icon: Eye,
                    bg: 'bg-yellow-50/50',
                    border: 'border-yellow-100',
                    text: 'text-yellow-700',
                    iconClass: 'text-yellow-600',
                    hint: 'Coach · ernst ≥45'
                  }, {
                    key: 'coach' as const,
                    label: 'Coach',
                    Icon: AlertTriangle,
                    bg: 'bg-blue-50/50',
                    border: 'border-blue-100',
                    text: 'text-blue-700',
                    iconClass: 'text-blue-600',
                    hint: 'Coach · ernst <45'
                  }, {
                    key: 'info' as const,
                    label: 'Info',
                    Icon: Info,
                    bg: 'bg-slate-50/50',
                    border: 'border-slate-100',
                    text: 'text-slate-700',
                    iconClass: 'text-slate-500',
                    hint: 'Observatie'
                  }].map(tier => {
                    const count = stats.severityCounts?.[tier.key] ?? 0;
                    const TierIcon = tier.Icon;
                    return <div key={tier.key} className={`flex flex-col p-3 rounded-xl border ${tier.bg} ${tier.border} transition-transform hover:scale-[1.02]`} title={tier.hint} data-id="element-5537" data-mp="2odi0pqbt5qfw">
                          
                            <TierIcon className={`w-5 h-5 mb-2 ${tier.iconClass}`} data-id="element-5538" data-mp="2ybxmrevza9ij" />
                          
                            <p className={`text-2xl font-bold leading-none ${tier.text}`} data-id="element-5539" data-mp="33b0qmw0829ox">
                            
                              {count}
                            </p>
                            <p className={`text-[11px] font-semibold uppercase tracking-wider ${tier.text} mt-1 opacity-80`} data-id="element-5540" data-mp="2alk0k502r4vv">
                            
                              {tier.label}
                            </p>
                          </div>;
                  })}
                    </div>
                  </div>

                  {/* Explainer legend */}
                  <div className="flex items-start gap-3 px-5 py-4 rounded-xl bg-blue-50/50 border border-blue-100" data-id="element-5541" data-mp="1muobvgqx0psu">
                    <Info className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" data-id="element-5542" data-mp="907iu1wwuws0" />
                    <div className="text-sm text-blue-800/90 leading-relaxed" data-id="element-5543" data-mp="8y4tlk3l17oc">
                      <span className="font-semibold text-blue-900" data-id="element-5544" data-mp="4znqrcr72jcq">
                        Hoe lees je deze cijfers?
                      </span>{' '}
                      <span className="font-semibold text-red-700" data-id="element-5545" data-mp="3r6voptzsbnyq">
                        Afgekeurd
                      </span>{' '}
                      en{' '}
                      <span className="font-semibold text-amber-700" data-id="element-5546" data-mp="3hgasuebhwxji">
                        Review
                      </span>{' '}
                      tellen het aantal <em data-id="element-5547" data-mp="3dfh6hpdpbqsq">vragen</em> met die status. Elke
                      vraag kan meerdere <em data-id="element-5548" data-mp="38gdz3ya918hl">bevindingen</em> hebben
                      (Kritiek/Blocker/Review/Coach/Info). De Pipeline Fasen
                      hierboven tonen het totaal aantal bevindingen per
                      controle-fase.
                    </div>
                  </div>

                  {/* System Coverage */}
                  <Card className="p-6 shadow-sm border-gray-200/60" data-id="element-5549" data-mp="2ot1879nuwcgg">
                    <div className="mb-6" data-id="element-5550" data-mp="2tt5ujgbg41el">
                      <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2" data-id="element-5551" data-mp="2tu76ro476txw">
                        <Database className="w-5 h-5 text-indigo-500" data-id="element-5552" data-mp="2fuxsitzjy9wk" />{' '}
                        Systeemdekking
                      </h3>
                      <p className="text-xs text-gray-500 mt-1" data-id="element-5553" data-mp="37jshkb7ug2uu">
                        Overzicht van beschikbare componenten en hun gebruik in
                        het vragenbestand.
                      </p>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8" data-id="element-5554" data-mp="2yrh37nq085v8">
                      <div className="bg-indigo-50/40 rounded-xl p-4 border border-indigo-100 text-center" data-id="element-5555" data-mp="32siu9h9rpbm4">
                        <p className="text-3xl font-bold text-indigo-700" data-id="element-5556" data-mp="3qid5xvicou4s">
                          {systemStats.visualTypeCount}
                        </p>
                        <p className="text-xs font-semibold uppercase tracking-wider text-indigo-600/80 mt-1" data-id="element-5557" data-mp="1jumm82u1awvx">
                          Visual Types
                        </p>
                      </div>
                      <div className="bg-purple-50/40 rounded-xl p-4 border border-purple-100 text-center" data-id="element-5558" data-mp="1tl8t98noqf30">
                        <p className="text-3xl font-bold text-purple-700" data-id="element-5559" data-mp="1olcoh775yjme">
                          {systemStats.questionTypeCount}
                        </p>
                        <p className="text-xs font-semibold uppercase tracking-wider text-purple-600/80 mt-1" data-id="element-5560" data-mp="10xv4hxu54ptz">
                          Vraagtypes
                        </p>
                      </div>
                      <div className="bg-blue-50/40 rounded-xl p-4 border border-blue-100 text-center" data-id="element-5561" data-mp="1arv81v9rxn0l">
                        <p className="text-3xl font-bold text-blue-700" data-id="element-5562" data-mp="2m9d1o1xn569e">
                          {systemStats.familyCount}
                        </p>
                        <p className="text-xs font-semibold uppercase tracking-wider text-blue-600/80 mt-1" data-id="element-5563" data-mp="2h9iarydv8goc">
                          Families
                        </p>
                      </div>
                      <div className="bg-emerald-50/40 rounded-xl p-4 border border-emerald-100 text-center" data-id="element-5564" data-mp="vt2fojpy9b0v">
                        <p className="text-3xl font-bold text-emerald-700" data-id="element-5565" data-mp="zsbazt0n1gyj">
                          {systemStats.moduleCount}
                        </p>
                        <p className="text-xs font-semibold uppercase tracking-wider text-emerald-600/80 mt-1" data-id="element-5566" data-mp="19sk95ce6xcgk">
                          Modules
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-8" data-id="element-5567" data-mp="1jj52akb3hos3">
                      <div data-id="element-5568" data-mp="1njozfnvo4se8">
                        <h4 className="text-xs font-bold text-gray-400 mb-4 uppercase tracking-wider" data-id="element-5569" data-mp="1nixgpdn66p5d">
                          Top 5 Vraagtypes
                        </h4>
                        <div className="space-y-2.5" data-id="element-5570" data-mp="1xgtlzotfepvk">
                          {Array.from(questionTypeDistribution.entries()).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([type, count]) => <div key={type} className="flex items-center justify-between text-sm bg-gray-50/50 px-3 py-2 rounded-lg border border-gray-100" data-id="element-5571" data-mp="22mgqowf3cs2d">
                          
                                <span className="text-gray-700 font-mono text-xs font-medium truncate mr-2" data-id="element-5572" data-mp="1xp72tl7coogn">
                                  {type}
                                </span>
                                <span className="font-bold text-gray-900 bg-white shadow-sm border border-gray-200 px-2 py-0.5 rounded text-xs" data-id="element-5573" data-mp="2qe5lqkjarun0">
                                  {count}
                                </span>
                              </div>)}
                        </div>
                      </div>
                      <div data-id="element-5574" data-mp="2citpu9teny4i">
                        <h4 className="text-xs font-bold text-gray-400 mb-4 uppercase tracking-wider" data-id="element-5575" data-mp="3932egjz1qf34">
                          Top 5 Visuals
                        </h4>
                        <div className="space-y-2.5" data-id="element-5576" data-mp="12ea8dqo9k8x8">
                          {Array.from(visualTypeDistribution.entries()).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([type, count]) => <div key={type} className="flex items-center justify-between text-sm bg-gray-50/50 px-3 py-2 rounded-lg border border-gray-100" data-id="element-5577" data-mp="17ctym39w46ve">
                          
                                <span className="text-gray-700 font-mono text-xs font-medium truncate mr-2" data-id="element-5578" data-mp="xm900fu7v2ch">
                                  {type}
                                </span>
                                <span className="font-bold text-gray-900 bg-white shadow-sm border border-gray-200 px-2 py-0.5 rounded text-xs" data-id="element-5579" data-mp="onwznm3jsq6u">
                                  {count}
                                </span>
                              </div>)}
                        </div>
                      </div>
                    </div>
                  </Card>
                </div>
              </motion.div>}
          </AnimatePresence>
        </section>
      </div>
    </div>;
}